import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Check, ChevronDown, ChevronUp, CircleCheckBig, Copy, QrCode } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import Decimal from 'decimal.js';
import { get } from 'lodash';


import TransferInstructions from 'components/TransferInstructions';
import { Button } from 'components/Button';
import { FormRow } from 'components/Form';
import GeneralError from 'components/GeneralError';
import Alert from 'components/Alert';
import Modal from 'components/Modal';
import Spinner from 'components/Spinner';
import Popover from 'components/Popover';
import useWorkflowGuard from 'hooks/useWorkflowGuard';
import { checkinAction, nextAction } from 'actions/workflow';
import { useConfig } from 'config/ConfigContext';
import { COLLATERAL_TERMS } from 'utils/constants';

let webSocket = null;
const NORMAL_CLOSE = 'Normal close';

const CollateralDeposit = ({
  navigate,
  searchParams,
  dispatch,
}) => {
  const { websocketUrl } = useConfig();
  useWorkflowGuard(searchParams, navigate);
  const [generalError, setGeneralError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isTransferInstructionsShown, setIsTransferInstructionsShown] = useState(true);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isQrOpen, setIsQrOpen] = useState(false);
  const termsRef = useRef(null);
  const workflow = useSelector((state) => get(state, 'workflow'));
  const applicationId = get(workflow, 'attributes.entityId');
  const checkinData = get(workflow, 'data');
  const remainingCollateral = useMemo(() => new Decimal(get(checkinData, 'collateralRequired') || 0).minus(get(checkinData, 'amountTransfered') || 0).minus(get(checkinData, 'amountPending') || 0), [checkinData]);
  const isCompleted = useMemo(() => new Decimal(get(checkinData, 'amountTransfered') || 0).greaterThanOrEqualTo(get(checkinData, 'collateralRequired') || 0), [checkinData]);

  const listenWebSocket = () => {
    webSocket = new WebSocket(`${websocketUrl}?application_id=${applicationId}`);
    console.log('connection:', webSocket);
    webSocket.addEventListener('message', (event) => {
      console.log('eventData:', event.data);
      const eventData = JSON.parse(get(event, 'data'));
      const eventType = get(eventData, 'EventType');
      if (eventType === 'CryptoLedgerTransferReceived' || eventType === 'CollateralFrozen') {
        handleRefresh();
      }
    });
    webSocket.addEventListener('close', (event) => {
      if (get(event, 'reason') !== NORMAL_CLOSE || get(event, 'code') !== 1000) {
        console.log('Closed and Reconnecting:', event);
        listenWebSocket();
      }
    });
  };

  useEffect(() => {
    if (applicationId) {
      listenWebSocket();
    }
  }, [applicationId]);

  const handleFail = (err) => {
    setGeneralError(get(err, 'data.failure.errors.0'));
    setIsSubmitting(false);
  };

  const toggleTransferInstructions = () => {
    setIsTransferInstructionsShown(!isTransferInstructionsShown);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setGeneralError(null);

    if (isCompleted) {
      setIsSubmitting(true);
      dispatch(nextAction({
        key: searchParams.get('key'),
        data: {},
        success: (res) => {
          navigate(get(res, 'data.url'));
          setIsSubmitting(false);
        },
        fail: handleFail,
      }));
    }
  };

  const copyWalletToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(get(checkinData, 'address'));
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleRefresh = () => {
    dispatch(checkinAction({
      key: searchParams.get('key'),
      data: {
        checkStatus: true,
      },
    }));
  }

  return (
    <div>
      <div className="bg-slate-50 rounded-lg border border-slate-200 mb-4">
        <button className="flex justify-between items-center w-full p-3" onClick={() => setIsExpanded(!isExpanded)}>
          <span className="text-sm text-slate-600 font-medium">Transfer Instructions</span>
          <div className="flex-shrink-0 ml-2 opacity-60 group-hover:opacity-80 transition-opacity">
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </button>
        {isExpanded ? <TransferInstructions collateralRequired={get(checkinData, 'collateralRequired') || 0} coin={get(checkinData, 'coin') || ''} /> : null}
      </div>

      <div className="bg-yellow-50 py-3 px-5 rounded-sm text-xs/5 text-slate-700 mb-5">
        By sending your crypto to this wallet, you are doing it subject to these <a ref={termsRef} className="underline font-medium text-blue-500" href="#" onClick={(e) => { e.preventDefault(); setIsTermsOpen(!isTermsOpen); }}>terms</a>. Sending any cryptocurrency other than Bitcoin (BTC) may result in permanent loss of the crypto sent.
      </div>

      {isTermsOpen && (
        <Popover isOpen onClose={() => setIsTermsOpen(false)} title={COLLATERAL_TERMS.title} targetRef={termsRef} placement="top">
          {COLLATERAL_TERMS.content}
        </Popover>
      )}

      <div className="mb-6">
        <h2 className="font-semibold mb-3">Wallet Address</h2>

        <div className="bg-slate-50 rounded-lg border border-slate-200 flex p-3 justify-between items-center gap-3">
          <span className="overflow-x-auto text-sm font-medium text-slate-500">{get(checkinData, 'address') || ''}</span>
          <div className="flex items-center gap-2">
            <button
              className="p-1 rounded-md bg-white border border-slate-200"
              onClick={copyWalletToClipboard}
              disabled={isCopied}
            >
              <span className="relative flex items-center justify-center w-[14px] h-[14px]">
                <Copy size={14} className={`absolute transition-opacity duration-300 ${isCopied ? 'opacity-0' : 'opacity-100'}`} />
                <Check size={14} className={`absolute transition-opacity duration-300 text-green-600 ${isCopied ? 'opacity-100' : 'opacity-0'}`} />
              </span>
            </button>
            <button className="p-1 rounded-md bg-white border border-slate-200" onClick={() => setIsQrOpen(true)}>
              <QrCode size={14} />
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 p-4 mb-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h2 className="font-semibold">Transfer Status</h2>
          <div className="flex gap-2 items-center">
            {isCompleted ? <CircleCheckBig className="text-green-600 h-5 w-5" /> : <Spinner color="blue-500" size={4} />}
            <span className="text-slate-500 text-sm">{isCompleted ? 'Complete' : (new Decimal(get(checkinData, 'amountPending') || 0).greaterThan(0) ? 'Verifying your transfer' : 'Waiting for crypto transfer')}</span>
          </div>
        </div>
        <div className="mb-2 h-2 rounded-full overflow-hidden bg-blue-200">
          <div className="h-full flex">
            <div
              className="h-full transition-all duration-700 ease-in-out bg-green-500"
              style={{ width: `${Math.min(100, new Decimal(get(checkinData, 'amountTransfered') || 0).div(new Decimal(get(checkinData, 'collateralRequired') || 1)).times(100).toNumber())}%` }}
            />
            <div
              className="h-full transition-all duration-700 ease-in-out bg-blue-700 animate-progress-stripes"
              style={{ width: `${Math.min(100, new Decimal(get(checkinData, 'amountPending') || 0).div(new Decimal(get(checkinData, 'collateralRequired') || 1)).times(100).toNumber())}%` }}
            />
          </div>
        </div>

        <div className="flex items-center justify-center gap-4 my-4">
          {new Decimal(get(checkinData, 'amountTransfered') || 0).greaterThan(0) && (
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm bg-green-500" />
              <span className="text-xs text-slate-600">Received</span>
            </div>
          )}
          {new Decimal(get(checkinData, 'amountPending') || 0).greaterThan(0) && (
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm bg-blue-700" style={{ backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.3) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.3) 75%, transparent 75%, transparent)', backgroundSize: '6px 6px' }} />
              <span className="text-xs text-slate-600">Verifying</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="rounded-lg border border-slate-200 py-4 px-4">
            <span className="block text-lg md:text-xl font-bold text-slate-800">{get(checkinData, 'collateralRequired') || 0} {(get(checkinData, 'coin') || '').toUpperCase()}</span>
            <span className="block font-semibold uppercase text-gray-500 text-[11px] tracking-wide mt-1">Required</span>
          </div>
          <div className="rounded-lg border border-slate-200 py-4 px-4">
            <span className="block text-lg md:text-xl font-bold text-green-600">{get(checkinData, 'amountTransfered') || 0} {(get(checkinData, 'coin') || '').toUpperCase()}</span>
            <span className="block font-semibold uppercase text-gray-500 text-[11px] tracking-wide mt-1">Received</span>
          </div>
          <div className="rounded-lg border border-slate-200 py-4 px-4">
            <span className="block text-lg md:text-xl font-bold text-slate-800">{get(checkinData, 'amountPending') || 0} {(get(checkinData, 'coin') || '').toUpperCase()}</span>
            <span className="block font-semibold uppercase text-gray-500 text-[11px] tracking-wide mt-1">Verifying</span>
          </div>
          <div className="rounded-lg border border-slate-200 py-4 px-4">
            <span className="block text-lg md:text-xl font-bold text-slate-800">{remainingCollateral.greaterThanOrEqualTo(0) ? remainingCollateral.toString() : 0} {(get(checkinData, 'coin') || '').toUpperCase()}</span>
            <span className="block font-semibold uppercase text-gray-500 text-[11px] tracking-wide mt-1">Remaining</span>
          </div>
        </div>

        <div className={`text-center p-6 rounded-lg ${isCompleted ? 'bg-[#EAF8EE]' : 'bg-blue-100'}`}>
          <span className="text-xs text-slate-800">
            {isCompleted
              ? 'Great news, your transfer is confirmed! Continue to Contract Signing to review and sign your loan agreement.'
              : 'Blockchain confirmations can take some time depending on network activity. You may close this tab and return later. We\'ll send you a text and email with a link once your transfer is confirmed so you can continue.'}
          </span>
        </div>
      </div>

      <Alert
        className="mb-6"
        title="Your security comes first. Always."
      >
        <p>Your collateral is held securely and protected. We safeguard your assets at every step.</p>
      </Alert>

      <GeneralError error={generalError} />

      <FormRow>
        <Button
          className={`w-full ${isCompleted ? 'animate-button-pulse' : ''}`}
          label="Next Step: Contract Signing"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          isDisabled={!isCompleted}
        />
      </FormRow>

      {isQrOpen ? (
        <Modal title="Wallet Address" onClose={() => setIsQrOpen(false)}>
          <div className="flex flex-col items-center gap-4 py-2">
            <QRCodeSVG value={get(checkinData, 'address') || ''} size={200} />
            <span className="text-xs text-slate-500 break-all text-center">{get(checkinData, 'address') || ''}</span>
          </div>
        </Modal>
      ) : null}

      {isTransferInstructionsShown ? (
        <Modal
          title="Transfer Instructions"
          onClose={toggleTransferInstructions}
        >
          <div className="flex flex-col gap-8">
            <TransferInstructions collateralRequired={get(checkinData, 'collateralRequired') || 0} coin={get(checkinData, 'coin') || ''} />
            <Button
              className="w-full"
              label="I Understand"
              onClick={toggleTransferInstructions}
            />
          </div>
        </Modal>
      ) : null}
    </div>
  );
};

CollateralDeposit.propTypes = {
  dispatch: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
};

export default CollateralDeposit;
